/**
 * BILLING HOOK
 * 
 * React hook for managing user billing state and usage tracking.
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import {
  UserBillingState,
  SubscriptionTier,
  CreditTransaction,
  CreditServiceType,
  TIER_CONFIGS,
  canCreateListing,
  hasRemainingQuota,
  getRemainingQuota,
} from '@/types/billing';

export interface ServiceCheckResult {
  allowed: boolean;
  reason: string;
  tier: SubscriptionTier;
  requiresPayment?: boolean;
  paymentAmountCents?: number;
}

export interface UseBillingReturn {
  // State
  billing: UserBillingState | null;
  transactions: CreditTransaction[];
  isLoading: boolean;
  error: string | null;

  // Derived state
  canCreateNewListing: boolean;
  isSubscriber: boolean;
  tierConfig: typeof TIER_CONFIGS[SubscriptionTier] | null;

  // Actions
  checkFeature: (feature: 'photoEdits' | 'virtualStagingProperties' | 'aiAdditions' | 'videos' | 'reedits') => ServiceCheckResult;
  refreshBilling: () => Promise<void>;
  fetchTransactions: (limit?: number) => Promise<void>;

  // Legacy compatibility
  checkService: (service: CreditServiceType, options?: { imageCount?: number }) => ServiceCheckResult;
}

export function useBilling(): UseBillingReturn {
  const { user } = useAuth();
  const [billing, setBilling] = useState<UserBillingState | null>(null);
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch billing data
  const fetchBilling = useCallback(async () => {
    if (!user?.id) {
      setBilling(null);
      setIsLoading(false);
      return;
    }

    try {
      setError(null);
      
      // First get user's own profile for credits and personal data
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select(`
          id,
          credit_balance,
          stripe_customer_id,
          stripe_subscription_id,
          monthly_credits_granted,
          one_off_kit_purchased,
          one_off_kit_photos_remaining,
          one_off_kit_videos_remaining,
          active_listings_count,
          monthly_reedits_used
        `)
        .eq('id', user.id)
        .single();

      if (profileError) {
        console.error('Error fetching profile:', profileError);
        setError('Failed to load billing information');
        return;
      }

      // Get team subscription info (uses team owner's plan if in a team)
      const { data: teamSubData, error: teamSubError } = await supabase
        .rpc('get_team_subscription');

      if (teamSubError) {
        console.error('Error fetching team subscription:', teamSubError);
      }

      const teamSub = teamSubData?.[0] as (NonNullable<typeof teamSubData>[number] & { monthly_reedits_used?: number }) | undefined;
      const profile = profileData as Record<string, unknown>;
      
      // Calculate usage period start (30 days rolling or from usage_period_start)
      const usagePeriodStart = teamSub?.usage_period_start 
        ? new Date(teamSub.usage_period_start)
        : new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
      
      // Count listings created in current usage period for the team (all team members)
      // If in a team, count all team member listings
      let periodListingsCount = 0;
      if (teamSub?.owner_id && teamSub.owner_id !== user.id) {
        // User is a team member - count team listings
        const { count } = await supabase
          .from('listings')
          .select('id', { count: 'exact', head: true })
          .gte('created_at', usagePeriodStart.toISOString());
        periodListingsCount = count ?? 0;
      } else {
        // User is owner or no team - count their own listings
        const { count } = await supabase
          .from('listings')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .gte('created_at', usagePeriodStart.toISOString());
        periodListingsCount = count ?? 0;
      }
      
      const actualListingsCount = periodListingsCount ?? 0;
      
      // Normalize tier: unknown/legacy values (e.g. the old 'pay_as_you_go' default)
      // map to the free tier.
      const rawTier = teamSub?.subscription_tier as string;
      const normalizedTier: SubscriptionTier = (rawTier && rawTier in TIER_CONFIGS)
        ? rawTier as SubscriptionTier
        : 'free';
      
      setBilling({
        userId: profile.id as string,
        subscriptionTier: normalizedTier,
        creditBalance: (profile.credit_balance as number) || 0,
        activeListingsCount: actualListingsCount,
        lifetimeListingsCount: (profile.active_listings_count as number) ?? 0,
        activeListingsLimit: teamSub?.active_listings_limit ?? null,
        stripeCustomerId: (profile.stripe_customer_id as string) || undefined,
        stripeSubscriptionId: (profile.stripe_subscription_id as string) || undefined,
        subscriptionStatus: (teamSub?.subscription_status as UserBillingState['subscriptionStatus']) || 'inactive',
        subscriptionPeriodStart: teamSub?.subscription_period_start ? new Date(teamSub.subscription_period_start) : undefined,
        subscriptionPeriodEnd: teamSub?.subscription_period_end ? new Date(teamSub.subscription_period_end) : undefined,
        monthlyCreditsGranted: (profile.monthly_credits_granted as number) || 0,
        oneOffKitPurchased: (profile.one_off_kit_purchased as boolean) || false,
        oneOffKitPhotosRemaining: (profile.one_off_kit_photos_remaining as number) || undefined,
        oneOffKitVideosRemaining: (profile.one_off_kit_videos_remaining as number) || undefined,
        monthlyPhotoEditsUsed: (teamSub?.monthly_photo_edits_used as number) || 0,
        monthlyStagingPropertiesUsed: (teamSub?.monthly_staging_properties_used as number) || 0,
        monthlyAiAdditionsUsed: (teamSub?.monthly_ai_additions_used as number) || 0,
        monthlyVideosUsed: (teamSub?.monthly_videos_used as number) || 0,
        monthlyReeditsUsed: ((teamSub?.monthly_reedits_used ?? profile.monthly_reedits_used) as number) || 0,
        usagePeriodStart: teamSub?.usage_period_start ? new Date(teamSub.usage_period_start) : undefined,
      });
    } catch (err) {
      console.error('Unexpected error fetching billing:', err);
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  // Fetch transaction history
  const fetchTransactions = useCallback(async (limit = 20) => {
    if (!user?.id) return;

    try {
      const { data, error: fetchError } = await supabase
        .from('credit_transactions')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (fetchError) {
        console.error('Error fetching transactions:', fetchError);
        return;
      }

      if (data) {
        setTransactions(
          data.map((t) => {
            const transaction = t as Record<string, unknown>;
            return {
              id: transaction.id as string,
              userId: transaction.user_id as string,
              serviceType: transaction.service_type as CreditServiceType,
              creditAmount: transaction.credit_amount as number,
              balanceAfter: transaction.balance_after as number,
              listingId: (transaction.listing_id as string) || undefined,
              photoIndex: (transaction.photo_index as number) || undefined,
              description: (transaction.description as string) || undefined,
              metadata: (transaction.metadata as Record<string, unknown>) || undefined,
              createdAt: new Date(transaction.created_at as string),
            };
          })
        );
      }
    } catch (err) {
      console.error('Unexpected error fetching transactions:', err);
    }
  }, [user?.id]);

  // Initial fetch
  useEffect(() => {
    fetchBilling();
  }, [fetchBilling]);

  // Real-time subscription for billing updates
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel(`billing:${user.id}:${Math.random().toString(36).slice(2)}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${user.id}`,
        },
        (payload) => {
          if (payload.new) {
            const data = payload.new as Record<string, unknown>;
            const rawTier = data.subscription_tier as string;
            const normalizedTier: SubscriptionTier = (rawTier && rawTier in TIER_CONFIGS)
              ? rawTier as SubscriptionTier
              : 'one_off_kit';
            setBilling((prev) => prev ? {
              ...prev,
              creditBalance: (data.credit_balance as number) ?? prev.creditBalance,
              activeListingsCount: (data.active_listings_count as number) ?? prev.activeListingsCount,
              lifetimeListingsCount: (data.active_listings_count as number) ?? prev.lifetimeListingsCount,
              subscriptionTier: normalizedTier,
              subscriptionStatus: (data.subscription_status as UserBillingState['subscriptionStatus']) || prev.subscriptionStatus,
              // Update monthly usage fields when they change
              monthlyPhotoEditsUsed: (data.monthly_photo_edits_used as number) ?? prev.monthlyPhotoEditsUsed,
              monthlyStagingPropertiesUsed: (data.monthly_staging_properties_used as number) ?? prev.monthlyStagingPropertiesUsed,
              monthlyAiAdditionsUsed: (data.monthly_ai_additions_used as number) ?? prev.monthlyAiAdditionsUsed,
              monthlyReeditsUsed: (data.monthly_reedits_used as number) ?? prev.monthlyReeditsUsed,
            } : null);
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'credit_transactions',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          if (payload.new) {
            const t = payload.new as Record<string, unknown>;
            setTransactions((prev) => [
              {
                id: t.id as string,
                userId: t.user_id as string,
                serviceType: t.service_type as CreditServiceType,
                creditAmount: t.credit_amount as number,
                balanceAfter: t.balance_after as number,
                listingId: (t.listing_id as string) || undefined,
                photoIndex: (t.photo_index as number) || undefined,
                description: (t.description as string) || undefined,
                metadata: (t.metadata as Record<string, unknown>) || undefined,
                createdAt: new Date(t.created_at as string),
              },
              ...prev,
            ]);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  // Check if user can use a feature
  const checkFeature = useCallback(
    (feature: 'photoEdits' | 'virtualStagingProperties' | 'aiAdditions' | 'videos' | 'reedits'): ServiceCheckResult => {
      if (!billing) {
        return {
          allowed: false,
          reason: 'Billing data not loaded',
          tier: 'one_off_kit' as SubscriptionTier,
          requiresPayment: false,
          paymentAmountCents: 0,
        };
      }

      const tierConfig = TIER_CONFIGS[billing.subscriptionTier] || TIER_CONFIGS['free'];
      const limit = tierConfig?.monthlyLimits?.[feature] ?? 0;

      // Videos are always free for subscribers
      if (feature === 'videos') {
        return {
          allowed: true,
          reason: 'Videos are free',
          tier: billing.subscriptionTier,
        };
      }

      // Check if feature is available
      if (limit === 0) {
        return {
          allowed: false,
          reason: 'Unlock this feature by upgrading your plan',
          tier: billing.subscriptionTier,
        };
      }

      // Check remaining quota
      const remaining = getRemainingQuota(billing, feature);

      if (remaining > 0) {
        return {
          allowed: true,
          reason: `${remaining} remaining this month`,
          tier: billing.subscriptionTier,
        };
      }

      return {
        allowed: false,
        reason: 'Great progress! You\'ve used your monthly quota. Upgrade to continue.',
        tier: billing.subscriptionTier,
      };
    },
    [billing]
  );

  // Legacy service check (for backwards compatibility)
  const checkService = useCallback(
    (service: CreditServiceType, options: { imageCount?: number } = {}): ServiceCheckResult => {
      // Map old service types to new feature types
      const serviceToFeature: Record<string, 'photoEdits' | 'virtualStagingProperties' | 'aiAdditions' | 'videos'> = {
        'PHOTO_EDIT': 'photoEdits',
        'P1_ENHANCEMENT': 'photoEdits',
        'P2_CLEANUP': 'photoEdits',
        'VIRTUAL_STAGING': 'virtualStagingProperties',
        'P3_STAGING': 'virtualStagingProperties',
        'AI_ADDITION': 'aiAdditions',
        'P4_GEN_FILL': 'aiAdditions',
        'VIDEO_GENERATION': 'videos',
      };

      const feature = serviceToFeature[service] || 'photoEdits';
      return checkFeature(feature);
    },
    [checkFeature]
  );

  // Derived state - with fallback for invalid tier values
  const canCreateNewListing = billing ? canCreateListing(billing) : false;
  const isSubscriber = billing?.subscriptionStatus === 'active';
  const tierConfig = billing 
    ? (TIER_CONFIGS[billing.subscriptionTier] || TIER_CONFIGS['free']) 
    : null;

  return {
    billing,
    transactions,
    isLoading,
    error,
    canCreateNewListing,
    isSubscriber,
    tierConfig,
    checkFeature,
    checkService,
    refreshBilling: fetchBilling,
    fetchTransactions,
  };
}
